import { MODULE_KEY, MODULE_LABELS, type AccessLevel, type ModuleKey } from '@gty/common';

import type { IconName } from '../ui/icons';

/**
 * THE module registry — one source for routes, nav and guard level (spec §7.2).
 *
 * Routes and sidebar links are both generated from this array, so a link and
 * the route behind it can never disagree about which permission they demand.
 *
 * Order, grouping and labels follow `Guruji ERP.dc.html` exactly. Three entries
 * the design's sidebar omits — Vehicles, Expenses, Notifications — are included
 * anyway: the seeded matrix grants Tour Manager and Accounts access to them, and
 * a permission that no link can reach is a permission nobody can verify.
 */

export type NavGroup = 'Overview' | 'Sales' | 'Finance' | 'Operations' | 'System';

export const NAV_GROUPS: readonly NavGroup[] = [
  'Overview',
  'Sales',
  'Finance',
  'Operations',
  'System',
] as const;

/** Counters the sidebar renders as a pill. Resolved from the service layer. */
export type BadgeKey = 'leads' | 'payments';

export interface ModuleRegistryEntry {
  module: ModuleKey;
  /** Absolute path, no trailing slash. */
  path: string;
  label: string;
  group: NavGroup;
  icon: IconName;
  /** Level needed to see the nav item and enter the route. */
  minLevel: AccessLevel;
  badge?: BadgeKey;
  /**
   * True for the three entries added from the permission matrix rather than
   * from the design. Surfaced so a designer reviewing the build can tell which
   * links are not in the mockup.
   */
  addedFromMatrix?: boolean;
}

export const MODULE_REGISTRY: readonly ModuleRegistryEntry[] = [
  {
    module: MODULE_KEY.DASHBOARD,
    path: '/dashboard',
    label: 'Dashboard',
    group: 'Overview',
    icon: 'dashboard',
    minLevel: 'VIEW',
  },

  {
    module: MODULE_KEY.LEAD_CRM,
    path: '/leads',
    label: 'Leads',
    group: 'Sales',
    icon: 'leads',
    minLevel: 'VIEW',
    badge: 'leads',
  },
  {
    module: MODULE_KEY.CUSTOMER_CRM,
    path: '/customers',
    label: 'Customers',
    group: 'Sales',
    icon: 'customers',
    minLevel: 'VIEW',
  },
  {
    module: MODULE_KEY.TOUR_MASTER,
    path: '/tours',
    label: 'Tour Master',
    group: 'Sales',
    icon: 'tours',
    minLevel: 'VIEW',
  },
  {
    module: MODULE_KEY.BOOKING_MANAGEMENT,
    path: '/bookings',
    label: 'Bookings',
    group: 'Sales',
    icon: 'bookings',
    minLevel: 'VIEW',
  },

  {
    module: MODULE_KEY.INVOICE_GENERATOR,
    path: '/invoices',
    label: 'Invoices',
    group: 'Finance',
    icon: 'invoices',
    minLevel: 'VIEW',
  },
  {
    module: MODULE_KEY.BOOKING_VOUCHER,
    path: '/vouchers',
    label: 'Vouchers',
    group: 'Finance',
    icon: 'vouchers',
    minLevel: 'VIEW',
  },
  {
    module: MODULE_KEY.CREDIT_NOTES,
    path: '/credit-notes',
    label: 'Credit Notes',
    group: 'Finance',
    icon: 'credit',
    minLevel: 'VIEW',
  },
  {
    module: MODULE_KEY.PAYMENTS_RECEIPTS,
    path: '/payments',
    label: 'Payments',
    group: 'Finance',
    icon: 'payments',
    minLevel: 'VIEW',
    badge: 'payments',
  },

  {
    module: MODULE_KEY.HOTEL_VENDOR_MANAGEMENT,
    path: '/vendors',
    label: 'Hotels & Vendors',
    group: 'Operations',
    icon: 'vendors',
    minLevel: 'VIEW',
  },
  {
    module: MODULE_KEY.VEHICLE_MANAGEMENT,
    path: '/vehicles',
    label: 'Vehicles',
    group: 'Operations',
    icon: 'vehicles',
    minLevel: 'VIEW',
    addedFromMatrix: true,
  },
  {
    module: MODULE_KEY.EXPENSE_TRACKING,
    path: '/expenses',
    label: 'Expenses',
    group: 'Operations',
    icon: 'expenses',
    minLevel: 'VIEW',
    addedFromMatrix: true,
  },
  {
    module: MODULE_KEY.REPORTS_ANALYTICS,
    path: '/reports',
    label: 'Reports',
    group: 'Operations',
    icon: 'reports',
    minLevel: 'VIEW',
  },
  {
    module: MODULE_KEY.WEBSITE,
    path: '/website',
    label: 'Website',
    group: 'Operations',
    icon: 'website',
    minLevel: 'VIEW',
    addedFromMatrix: true,
  },

  {
    module: MODULE_KEY.NOTIFICATIONS,
    path: '/notifications',
    label: 'Notifications',
    group: 'System',
    icon: 'bell',
    minLevel: 'VIEW',
    addedFromMatrix: true,
  },
  {
    module: MODULE_KEY.SETTINGS_ROLES,
    path: '/settings/roles',
    label: 'Settings & Roles',
    group: 'System',
    icon: 'settings',
    minLevel: 'VIEW',
  },
] as const;

/**
 * Design entries with no `ModuleKey`.
 *
 * The mockup's sidebar shows Credit Notes and Customer Portal, but the
 * authoritative permission matrix has neither, so there is nothing to guard
 * them WITH. Rather than invent a mapping — which would silently grant access
 * under some other module's permission — they render visibly disabled with the
 * reason attached. Giving them a real `ModuleKey` is a `@gty/common` + Prisma
 * enum + seed change; until then this is the honest state.
 */
export interface PendingNavEntry {
  key: string;
  label: string;
  group: NavGroup | 'Footer';
  icon: IconName;
  reason: string;
}

export const PENDING_NAV: readonly PendingNavEntry[] = [
  // Credit Notes GRADUATED from here on 2026-08-01: it now has a real
  // `CREDIT_NOTES` ModuleKey, a seeded permission row and a screen, so it lives
  // in MODULE_REGISTRY above and is guarded like everything else.
  {
    key: 'CUSTOMER_PORTAL',
    label: 'Customer Portal',
    group: 'Footer',
    icon: 'portal',
    reason: 'Phase 2 — the customer-facing portal is out of scope for this build.',
  },
] as const;

export const CUSTOMER_PORTAL_ENTRY: PendingNavEntry = PENDING_NAV[1] ?? {
  key: 'CUSTOMER_PORTAL',
  label: 'Customer Portal',
  group: 'Footer',
  icon: 'portal',
  reason: 'Phase 2.',
};

/** The landing route after login, and the target of `/`. */
export const DEFAULT_ROUTE = '/dashboard';

export function findEntryByPath(pathname: string): ModuleRegistryEntry | undefined {
  // Longest path first so `/settings/roles` wins over a future `/settings`.
  return [...MODULE_REGISTRY]
    .sort((a, b) => b.path.length - a.path.length)
    .find((entry) => pathname === entry.path || pathname.startsWith(`${entry.path}/`));
}

/** Full matrix label (not the shortened nav label) — used by ComingSoon/Forbidden. */
export function moduleLabel(module: ModuleKey): string {
  return MODULE_LABELS[module];
}
